import { api } from '../../../api/client';

export const datamartApi = {
    /**
    * Retrieves the data dictionary for all databases or a specific database.
    * @param {string|null} dbName - Optional database name.
     */
    getDictionary: async (dbName = null) => {
        if (dbName) {
            return api.get(`/datamart/dictionary/${dbName}`);
        }
        return api.get('/datamart/dictionary');
    },

    /**
    * Retrieves a sample of records from a table for preview.
    * @param {string} dbName - Database name.
    * @param {string} tableName - Table name.
    * @param {number} limit - Number of sample records.
     */
    getTableSample: async (dbName, tableName, limit = 10) => {
        return api.get(`/datamart/dictionary/${dbName}/${tableName}/sample?limit=${limit}`);
    },

    /**
    * Retrieves the rules and queries authorized for AI access.
     */
    getAiRules: async () => {
        return api.get('/datamart/ai-config/rules');
    },

    /**
    * Creates a new AI query rule.
     */
    createAiRule: async (payload) => {
        return api.post('/datamart/ai-config/rules', payload);
    },

    /**
    * Updates an AI rule and its dynamic filters.
     */
    updateAiRule: async (ruleId, payload) => {
        return api.put(`/datamart/ai-config/rules/${ruleId}`, payload);
    },

    /**
    * Dynamically enables or disables AI access to a dataset.
     */
    toggleAiRule: async (ruleId, enabled) => {
        return api.patch(`/datamart/ai-config/rules/${ruleId}/toggle`, { enabled });
    },

    /**
    * Deletes an AI access rule.
     */
    deleteAiRule: async (ruleId) => {
        return api.delete(`/datamart/ai-config/rules/${ruleId}`);
    },

    /**
    * Tests an authorized AI query to verify which data it returns.
     */
    testAiRule: async (ruleId) => {
        return api.post(`/datamart/ai-config/test/${ruleId}`, {});
    },

    /**
    * Previews rule data in real time without saving the rule.
     * @param {Object} payload - { target_db, target_table, selected_columns, filter_rules, limit }
     */
    previewAiRule: async (payload) => {
        return api.post('/datamart/ai-config/preview', payload);
    },

    /**
    * Retrieves the audit log of queries executed by AI.
     */
    getAiAuditLogs: async (limit = 50) => {
        return api.get(`/datamart/ai-config/logs?limit=${limit}`);
    },

    /**
    * Retrieves data from a predefined view.
    * @param {string} viewName - View name (e.g. "users", "emitted_certificates").
    * @param {number} limit - Maximum number of results.
    * @param {number} offset - Result offset.
     */
    getViews: async (viewName, limit = 100, offset = 0) => {
        return api.get(`/datamart/views/${viewName}?limit=${limit}&offset=${offset}`);
    },

    /**
    * Executes a dynamic Datamart query.
    * @param {Object} queryPayload - JSON object containing the query structure.
    * Example: { db: "identity_db", table: "users", select: ["id", "username"] }
     */
    postQuery: async (queryPayload) => {
        return api.post('/datamart/query', queryPayload);
    },

    /**
    * Retrieves the formal Redis data dictionary specification and server status.
     */
    getRedisDictionary: async () => {
        return api.get('/datamart/redis/dictionary');
    },

    /**
    * Inspects active keys in the Redis instance in real time.
    * @param {string} pattern - Search pattern (e.g. '*', 'pwd_reset:*', 'request_rate_limiter.*').
    * @param {number} limit - Maximum number of keys to inspect.
     */
    getRedisLiveKeys: async (pattern = '*', limit = 100) => {
        return api.get(`/datamart/redis/live-keys?pattern=${encodeURIComponent(pattern)}&limit=${limit}`);
    }
};


